import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Briefcase, MapPin, Clock, ChevronRight, X, Upload, CheckCircle,
  Lightbulb, Zap, Target, Globe, Users, Star,
  ArrowRight, Send, User, Mail, Phone, FileText,
  Building2, Award, Heart, Rocket,
} from 'lucide-react';
import { Verify } from 'react-puzzle-captcha';
import 'react-puzzle-captcha/dist/react-puzzle-captcha.css';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import usePageMeta from '../hooks/usePageMeta';

const JOBS_API  = 'https://fundvisercapital.in/app/careers-jobs-api.php';
const APPLY_API = 'https://fundvisercapital.in/app/careers-apply-api.php';

/* ─── Fallback static data (shown if API fails) ─── */
const OPEN_POSITIONS = [
  {
    id: 1,
    title: 'Equity Research Analyst',
    department: 'Research & Strategy',
    location: 'Mumbai, Maharashtra',
    type: 'Full-Time',
    experience: '2–5 Years',
    badge: 'Hot',
    badgeColor: 'bg-red-500',
    description:
      'Drive in-depth research on listed equities, build financial models, and deliver actionable insights that directly influence investment decisions at a BSE-listed firm.',
    responsibilities: [
      'Conduct fundamental and technical analysis of BSE/NSE listed securities',
      'Build and maintain detailed financial models (DCF, comparative analysis)',
      'Prepare sector-specific research reports and investment notes',
      'Monitor macroeconomic trends and market-moving events',
      'Collaborate with the portfolio team to refine investment thesis',
    ],
    requirements: [
      'CA / MBA (Finance) / CFA Level I or above',
      '2–5 years in equity research or investment analysis',
      'Proficiency in Bloomberg, Excel, and financial databases',
      'Strong written and oral communication skills',
      'Passion for markets and data-driven decision making',
    ],
  },
  {
    id: 2,
    title: 'Business Development Executive',
    department: 'Sales & Growth',
    location: 'Mumbai, Maharashtra',
    type: 'Full-Time',
    experience: '1–3 Years',
    badge: 'New',
    badgeColor: 'bg-green-500',
    description:
      'Identify and develop strategic relationships, drive revenue growth, and expand our client portfolio across institutional and HNI segments.',
    responsibilities: [
      'Identify and pursue new business opportunities in the financial services segment',
      'Build and nurture long-term client relationships with HNIs and institutions',
      'Represent Fundviser Capital at industry events and investor meets',
      'Track pipeline metrics and report on revenue targets monthly',
      'Co-create marketing collateral with the strategy team',
    ],
    requirements: [
      "Bachelor's / MBA in Business, Finance, or Marketing",
      '1–3 years in business development, sales, or client relations',
      'Strong network in financial services is a plus',
      'Excellent interpersonal and negotiation skills',
      'Self-driven with a target-oriented mindset',
    ],
  },
  {
    id: 3,
    title: 'Compliance & Secretarial Officer',
    department: 'Legal & Compliance',
    location: 'Mumbai, Maharashtra',
    type: 'Full-Time',
    experience: '3–6 Years',
    badge: 'Urgent',
    badgeColor: 'bg-yellow-500',
    description:
      'Ensure end-to-end regulatory compliance for a publicly listed company, manage secretarial functions, and liaise with SEBI, BSE, and statutory authorities.',
    responsibilities: [
      'Handle all BSE/SEBI filings, disclosures, and regulatory submissions',
      'Maintain statutory registers and corporate records per Companies Act',
      'Organize Board, Committee, and General Meetings end-to-end',
      'Monitor changes in SEBI, LODR, and Companies Act regulations',
      'Coordinate with external auditors, legal counsel, and regulators',
    ],
    requirements: [
      'Company Secretary (CS) — qualified member of ICSI',
      '3–6 years in compliance or secretarial role at a listed company',
      'Deep knowledge of SEBI LODR, Companies Act 2013, and FEMA',
      'Meticulous attention to detail and deadline management',
      'Proficiency in MCA21, NEAPS, and BSE Listing Centre portals',
    ],
  },
];


/* ─── Culture pillars ─── */
const CULTURE = [
  { icon: Lightbulb, title: 'Innovation First',      desc: 'Every idea is welcome. We experiment, iterate, and build better — constantly.' },
  { icon: Target,    title: 'Ownership Mindset',     desc: 'We hire adults who take pride in outcomes, not just output.' },
  { icon: Zap,       title: 'Fast-Paced Execution',  desc: 'We move quickly, make decisions with data, and bias strongly toward action.' },
  { icon: Globe,     title: 'Learning Environment',  desc: 'Weekly reads, lunch-and-learns, and a library of resources at your fingertips.' },
  { icon: Users,     title: 'Collaborative Teams',   desc: 'Flat hierarchy, open doors, and cross-functional collaboration every day.' },
  { icon: Heart,     title: 'People-First Culture',  desc: 'Mental health, team outings, and genuine care for every person on the team.' },
];

/* ─── Testimonials ─── */
const TESTIMONIALS = [
  {
    name: 'Priya Sharma',
    role: 'Senior Research Analyst',
    tenure: '3 years at Fundviser Capital',
    quote:
      'I joined as a fresher and was handed real responsibilities on day one. The exposure to listed markets, the mentorship, and the pace of growth here is unmatched.',
    initials: 'PS',
    gradient: 'from-violet-500 to-purple-600',
  },
  {
    name: 'Rahul Mehta',
    role: 'Business Development Manager',
    tenure: '2 years at Fundviser',
    quote:
      'What I love most is the culture of ownership. My ideas were implemented within weeks. This is the kind of environment where you grow faster than you imagined.',
    initials: 'RM',
    gradient: 'from-blue-500 to-cyan-600',
  },
  {
    name: 'Ananya Joshi',
    role: 'Compliance Officer',
    tenure: '4 years at Fundviser',
    quote:
      'Working at a BSE-listed company gives you unparalleled depth in regulatory expertise. The team is brilliant, supportive, and there\'s always something new to learn.',
    initials: 'AJ',
    gradient: 'from-green-500 to-emerald-600',
  },
];

/* ─── Stats ─── */
const STATS = [
  { value: '15+', label: 'Years of Excellence' },
  { value: '50+', label: 'Team Members' },
  { value: 'BSE', label: 'Listed Company' },
  { value: '100%', label: 'Growth Mindset' },
];

/* ═══════════════════════════════════════════════
   Apply Modal
═══════════════════════════════════════════════ */
const ApplyModal = ({ job, onClose }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', coverLetter: '' });
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [captchaChecked, setCaptcha] = useState(false);
  const [captchaKey, setCaptchaKey] = useState(0);
  const fileRef = useRef();

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleDrop = useCallback(e => {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) setFile(dropped);
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    if (!captchaChecked) { setError('Please complete the verification to prove you are not a robot.'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('name',         form.name);
      fd.append('email',        form.email);
      fd.append('phone',        form.phone);
      fd.append('coverLetter',  form.coverLetter);
      fd.append('job_id',       job.id);
      fd.append('job_title',    job.title);
      if (file) fd.append('cv', file);

      const res  = await fetch(APPLY_API, { method: 'POST', body: fd });
      const data = await res.json();
      if (data.success) setSubmitted(true);
      else setError(data.message || 'Something went wrong. Please try again.');
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-3xl shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-start justify-between p-6 pb-4 bg-white border-b border-gray-100 rounded-t-3xl">
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-[#360153] mb-1">Apply Now</p>
            <h3 className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>{job.title}</h3>
            <p className="text-sm text-gray-400 mt-0.5">{job.department} · {job.location}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="p-6">
          {submitted ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Application Submitted!</h4>
              <p className="text-gray-500 max-w-sm mx-auto leading-relaxed">
                Thank you for your interest in the <strong>{job.title}</strong> role. Our team will review your application and reach out within 5–7 business days.
              </p>
              <button
                onClick={onClose}
                className="mt-8 px-8 py-3 rounded-xl text-white font-bold text-sm transition-all duration-200 hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #360153 0%, #5a0280 100%)' }}
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Full Name *</label>
                  <div className="relative">
                    <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text" name="name" required value={form.name} onChange={handleChange}
                      placeholder="John Doe"
                      className="w-full pl-10 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#360153] focus:bg-white focus:ring-4 focus:ring-[#360153]/8 transition-all"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Phone Number *</label>
                  <div className="relative">
                    <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="tel" name="phone" required value={form.phone} onChange={handleChange}
                      placeholder="+91 98765 43210"
                      className="w-full pl-10 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#360153] focus:bg-white focus:ring-4 focus:ring-[#360153]/8 transition-all"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Email Address *</label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="email" name="email" required value={form.email} onChange={handleChange}
                    placeholder="john@example.com"
                    className="w-full pl-10 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#360153] focus:bg-white focus:ring-4 focus:ring-[#360153]/8 transition-all"
                  />
                </div>
              </div>

              {/* CV Upload */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Upload CV / Resume *</label>
                <div
                  onDragOver={e => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={handleDrop}
                  onClick={() => fileRef.current.click()}
                  className={`relative cursor-pointer rounded-2xl border-2 border-dashed p-8 text-center transition-all duration-200
                    ${dragging ? 'border-[#360153] bg-[#360153]/5 scale-[1.01]' : file ? 'border-green-400 bg-green-50' : 'border-gray-200 bg-gray-50 hover:border-[#360153]/50 hover:bg-[#360153]/3'}`}
                >
                  <input
                    ref={fileRef} type="file" accept=".pdf,.doc,.docx" className="hidden"
                    onChange={e => setFile(e.target.files[0])}
                  />
                  {file ? (
                    <div className="flex items-center justify-center gap-3">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                      <div className="text-left">
                        <p className="text-sm font-bold text-green-700">{file.name}</p>
                        <p className="text-xs text-green-500">{(file.size / 1024).toFixed(0)} KB · Click to replace</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="w-12 h-12 rounded-2xl bg-[#360153]/10 flex items-center justify-center mx-auto mb-3">
                        <Upload className="w-6 h-6 text-[#360153]" />
                      </div>
                      <p className="text-sm font-bold text-gray-700">Drag & drop your CV here</p>
                      <p className="text-xs text-gray-400 mt-1">or <span className="text-[#360153] font-bold">click to browse</span> · PDF, DOC, DOCX up to 5MB</p>
                    </>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Cover Letter <span className="normal-case font-medium text-gray-400">(optional)</span></label>
                <textarea
                  name="coverLetter" value={form.coverLetter} onChange={handleChange} rows={4}
                  placeholder="Tell us why you're excited about this role and what makes you the right fit..."
                  className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#360153] focus:bg-white focus:ring-4 focus:ring-[#360153]/8 transition-all resize-none"
                />
              </div>

              {/* CAPTCHA */}
              {captchaChecked ? (
                <div className="flex items-center gap-3 px-4 py-3.5 rounded-xl"
                  style={{ background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)', border: '1.5px solid #86efac' }}>
                  <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-green-800">Verified Successfully</p>
                    <p className="text-xs text-green-600">You're confirmed as human</p>
                  </div>
                  <button type="button"
                    onClick={() => { setCaptcha(false); setCaptchaKey(k => k + 1); }}
                    className="text-xs text-green-500 hover:text-green-700 font-medium transition-colors">
                    Reset
                  </button>
                </div>
              ) : (
                <button type="button"
                  onClick={() => setCaptchaKey(k => k + 1)}
                  className="w-full flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all duration-200 hover:shadow-md active:scale-[0.99] group"
                  style={{ background: '#f8f4ff', border: '1.5px solid #e8d8f8' }}>
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform duration-200 group-hover:scale-110"
                    style={{ background: 'rgba(54,1,83,0.1)' }}>
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#360153" strokeWidth="2.5">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    </svg>
                  </div>
                  <span className="text-sm font-semibold text-gray-700 flex-1 text-left">I'm not a robot</span>
                  <div className="text-right flex-shrink-0">
                    <p className="text-[10px] font-black text-gray-400 leading-none tracking-wide">CLICK &VERIFY</p>
                    <p className="text-[9px] text-gray-300 mt-0.5">Slide to confirm</p>
                  </div>
                </button>
              )}

              {!captchaChecked && captchaKey > 0 && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center"
                  style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(6px)' }}
                  onClick={e => { if (e.target === e.currentTarget) setCaptchaKey(0); }}>
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{ background: '#fff', border: '1px solid #e8d8f8', maxWidth: '95vw' }}>
                    <div className="flex items-center justify-between px-5 py-3.5"
                      style={{ background: 'linear-gradient(135deg, #1a002b, #360153)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                      <div className="flex items-center gap-2.5">
                        <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                        </svg>
                        <span className="text-white text-sm font-bold">Security Verification</span>
                      </div>
                      <button type="button" onClick={() => setCaptchaKey(0)} className="text-white/50 hover:text-white transition-colors text-lg leading-none">✕</button>
                    </div>
                    <div className="p-1">
                      <Verify key={captchaKey} width={320} height={160} visible={true}
                        onSuccess={() => { setCaptcha(true); setCaptchaKey(0); }}
                        onFail={() => setCaptcha(false)}
                        onRefresh={() => setCaptchaKey(k => k + 1)} />
                    </div>
                    <p className="text-center text-[10px] text-gray-400 pb-3">Drag the slider to complete the puzzle</p>
                  </div>
                </div>
              )}

              {error && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                  <span className="text-red-500 text-sm">⚠</span>
                  <p className="text-sm text-red-600 font-medium">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl text-white font-black tracking-wide text-sm transition-all duration-300 hover:scale-[1.02] hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                style={{ background: 'linear-gradient(135deg, #360153 0%, #5a0280 100%)', boxShadow: '0 8px 24px rgba(54,1,83,0.35)' }}
              >
                {loading
                  ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Submitting…</>
                  : <><Send className="w-4 h-4" /> Submit Application</>}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   Job Detail Drawer
═══════════════════════════════════════════════ */
const JobDrawer = ({ job, onClose, onApply }) => (
  <div className="fixed inset-0 z-[90] flex justify-end" onClick={onClose}>
    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
    <div
      className="relative w-full max-w-xl h-full bg-white shadow-2xl overflow-y-auto"
      onClick={e => e.stopPropagation()}
      style={{ animation: 'slideInRight 0.3s ease-out' }}
    >
      <div className="sticky top-0 bg-white z-10 px-6 pt-6 pb-4 border-b border-gray-100 flex items-start justify-between">
        <div>
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-white text-xs font-bold mb-2 ${job.badgeColor}`}>{job.badge}</span>
          <h3 className="text-xl font-black text-gray-900">{job.title}</h3>
          <p className="text-sm text-[#360153] font-semibold mt-0.5">{job.department}</p>
        </div>
        <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 transition-colors mt-1">
          <X className="w-5 h-5 text-gray-400" />
        </button>
      </div>

      <div className="p-6 space-y-6">
        <div className="flex flex-wrap gap-3">
          {[
            { icon: MapPin, label: job.location },
            { icon: Clock,  label: job.type },
            { icon: Briefcase, label: job.experience },
          ].map(({ icon: Icon, label }) => (
            <span key={label} className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-lg text-xs font-semibold text-gray-600">
              <Icon className="w-3.5 h-3.5" /> {label}
            </span>
          ))}
        </div>

        <div>
          <h4 className="text-sm font-black text-gray-900 uppercase tracking-wider mb-2">About the Role</h4>
          <p className="text-sm text-gray-600 leading-relaxed">{job.description}</p>
        </div>

        <div>
          <h4 className="text-sm font-black text-gray-900 uppercase tracking-wider mb-3">Key Responsibilities</h4>
          <ul className="space-y-2">
            {job.responsibilities.map((r, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-gray-600">
                <span className="w-5 h-5 rounded-full bg-[#360153]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-3 h-3 text-[#360153]" />
                </span>
                {r}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-sm font-black text-gray-900 uppercase tracking-wider mb-3">Requirements</h4>
          <ul className="space-y-2">
            {job.requirements.map((r, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-gray-600">
                <span className="w-5 h-5 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Star className="w-3 h-3 text-yellow-500" />
                </span>
                {r}
              </li>
            ))}
          </ul>
        </div>

        <button
          onClick={onApply}
          className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl text-white font-black text-sm tracking-wide transition-all duration-300 hover:scale-[1.02] hover:shadow-xl"
          style={{ background: 'linear-gradient(135deg, #360153 0%, #5a0280 100%)', boxShadow: '0 8px 24px rgba(54,1,83,0.35)' }}
        >
          Apply for this Role <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════
   Open Application Form
═══════════════════════════════════════════════ */
const OpenApplicationForm = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', role: '', message: '' });
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [captchaChecked, setCaptcha] = useState(false);
  const [captchaKey, setCaptchaKey] = useState(0);
  const fileRef = useRef();

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleDrop = useCallback(e => {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) setFile(dropped);
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    if (!captchaChecked) { setError('Please complete the verification to prove you are not a robot.'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('name',        form.name);
      fd.append('email',       form.email);
      fd.append('phone',       form.phone);
      fd.append('coverLetter', form.message);
      fd.append('job_title',   form.role || 'Open Application');
      if (file) fd.append('cv', file);

      const res  = await fetch(APPLY_API, { method: 'POST', body: fd });
      const data = await res.json();
      if (data.success) setSubmitted(true);
      else setError(data.message || 'Something went wrong. Please try again.');
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#360153] focus:bg-white focus:ring-4 focus:ring-[#360153]/8 transition-all duration-200";
  const labelCls = "block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2";

  if (submitted) {
    return (
      <div className="text-center py-16">
        <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-12 h-12 text-green-600" />
        </div>
        <h4 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>You're on our radar!</h4>
        <p className="text-gray-500 max-w-md mx-auto leading-relaxed text-lg">
          Thank you for reaching out. We'll keep your profile on file and reach out when the right opportunity opens up.
        </p>
        <button
          onClick={() => { setSubmitted(false); setForm({ name: '', email: '', phone: '', role: '', message: '' }); setFile(null); }}
          className="mt-8 px-8 py-3 rounded-xl text-white font-bold text-sm transition-all duration-200 hover:scale-105"
          style={{ background: 'linear-gradient(135deg, #360153 0%, #5a0280 100%)' }}
        >
          Submit Another
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className={labelCls}>Full Name *</label>
          <div className="relative">
            <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" name="name" required value={form.name} onChange={handleChange}
              className={`${inputCls} pl-10`} placeholder="John Doe" />
          </div>
        </div>
        <div>
          <label className={labelCls}>Phone Number *</label>
          <div className="relative">
            <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="tel" name="phone" required value={form.phone} onChange={handleChange}
              className={`${inputCls} pl-10`} placeholder="+91 98765 43210" />
          </div>
        </div>
      </div>

      <div>
        <label className={labelCls}>Email Address *</label>
        <div className="relative">
          <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="email" name="email" required value={form.email} onChange={handleChange}
            className={`${inputCls} pl-10`} placeholder="john@example.com" />
        </div>
      </div>

      <div>
        <label className={labelCls}>Role / Area of Interest</label>
        <div className="relative">
          <FileText className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" name="role" value={form.role} onChange={handleChange}
            className={`${inputCls} pl-10`} placeholder="e.g. Finance, Operations, Technology..." />
        </div>
      </div>

      {/* Drag & Drop CV */}
      <div>
        <label className={labelCls}>Upload CV / Resume *</label>
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileRef.current.click()}
          className={`relative cursor-pointer rounded-2xl border-2 border-dashed p-10 text-center transition-all duration-200
            ${dragging ? 'border-[#360153] bg-[#360153]/5 scale-[1.01]' : file ? 'border-green-400 bg-green-50' : 'border-gray-200 bg-gray-50 hover:border-[#360153]/50 hover:bg-[#360153]/3'}`}
        >
          <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" className="hidden"
            onChange={e => setFile(e.target.files[0])} />
          {file ? (
            <div className="flex items-center justify-center gap-3">
              <CheckCircle className="w-7 h-7 text-green-600" />
              <div className="text-left">
                <p className="text-sm font-bold text-green-700">{file.name}</p>
                <p className="text-xs text-green-500">{(file.size / 1024).toFixed(0)} KB · Click to replace</p>
              </div>
            </div>
          ) : (
            <>
              <div className="w-16 h-16 rounded-2xl bg-[#360153]/10 flex items-center justify-center mx-auto mb-4">
                <Upload className="w-8 h-8 text-[#360153]" />
              </div>
              <p className="font-bold text-gray-700">Drag & drop your CV here</p>
              <p className="text-sm text-gray-400 mt-1">
                or <span className="text-[#360153] font-bold">click to browse</span>
              </p>
              <p className="text-xs text-gray-300 mt-2">PDF, DOC, DOCX · Max 5MB</p>
            </>
          )}
        </div>
      </div>

      <div>
        <label className={labelCls}>Tell Us About Yourself <span className="normal-case font-medium text-gray-400">(optional)</span></label>
        <textarea name="message" value={form.message} onChange={handleChange} rows={4}
          placeholder="Share your background, what you're passionate about, and what kind of role you're looking for..."
          className={`${inputCls} resize-none`} />
      </div>

      {/* CAPTCHA */}
      {captchaChecked ? (
        <div className="flex items-center gap-3 px-4 py-3.5 rounded-xl"
          style={{ background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)', border: '1.5px solid #86efac' }}>
          <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
            <CheckCircle className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold text-green-800">Verified Successfully</p>
            <p className="text-xs text-green-600">You're confirmed as human</p>
          </div>
          <button type="button"
            onClick={() => { setCaptcha(false); setCaptchaKey(k => k + 1); }}
            className="text-xs text-green-500 hover:text-green-700 font-medium transition-colors">
            Reset
          </button>
        </div>
      ) : (
        <button type="button"
          onClick={() => setCaptchaKey(k => k + 1)}
          className="w-full flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all duration-200 hover:shadow-md active:scale-[0.99] group"
          style={{ background: '#f8f4ff', border: '1.5px solid #e8d8f8' }}>
          <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform duration-200 group-hover:scale-110"
            style={{ background: 'rgba(54,1,83,0.1)' }}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#360153" strokeWidth="2.5">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
          </div>
          <span className="text-sm font-semibold text-gray-700 flex-1 text-left">I'm not a robot</span>
          <div className="text-right flex-shrink-0">
            <p className="text-[10px] font-black text-gray-400 leading-none tracking-wide">CLICK & VERIFY</p>
            <p className="text-[9px] text-gray-300 mt-0.5">Slide to confirm</p>
          </div>
        </button>
      )}

      {!captchaChecked && captchaKey > 0 && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(6px)' }}
          onClick={e => { if (e.target === e.currentTarget) setCaptchaKey(0); }}>
          <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{ background: '#fff', border: '1px solid #e8d8f8', maxWidth: '95vw' }}>
            <div className="flex items-center justify-between px-5 py-3.5"
              style={{ background: 'linear-gradient(135deg, #1a002b, #360153)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <div className="flex items-center gap-2.5">
                <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                <span className="text-white text-sm font-bold">Security Verification</span>
              </div>
              <button type="button" onClick={() => setCaptchaKey(0)} className="text-white/50 hover:text-white transition-colors text-lg leading-none">✕</button>
            </div>
            <div className="p-1">
              <Verify key={captchaKey} width={320} height={160} visible={true}
                onSuccess={() => { setCaptcha(true); setCaptchaKey(0); }}
                onFail={() => setCaptcha(false)}
                onRefresh={() => setCaptchaKey(k => k + 1)} />
            </div>
            <p className="text-center text-[10px] text-gray-400 pb-3">Drag the slider to complete the puzzle</p>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <span className="text-red-500 text-sm">⚠</span>
          <p className="text-sm text-red-600 font-medium">{error}</p>
        </div>
      )}

      <button type="submit"
        disabled={loading}
        className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl text-white font-black tracking-wide text-sm transition-all duration-300 hover:scale-[1.02] hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
        style={{ background: 'linear-gradient(135deg, #360153 0%, #5a0280 100%)', boxShadow: '0 8px 24px rgba(54,1,83,0.35)' }}>
        {loading
          ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Submitting…</>
          : <><Rocket className="w-4 h-4" /> Submit My Application</>}
      </button>
    </form>
  );
};

/* ═══════════════════════════════════════════════
   MAIN CAREERS PAGE
═══════════════════════════════════════════════ */
const Careers = () => {
  usePageMeta(
    "Careers",
    "Explore open positions at Fundviser Capital (India) Ltd. and apply to join a Mumbai-based, BSE-listed investment company."
  );
  const [activeJob, setActiveJob] = useState(null);
  const [applyJob, setApplyJob]   = useState(null);
  const [jobs, setJobs]           = useState(OPEN_POSITIONS);
  const [jobsLoading, setJobsLoading] = useState(true);

  useEffect(() => {
    fetch(JOBS_API)
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setJobs(data.map(j => ({ ...j, badgeColor: j.badge_color })));
        }
      })
      .catch(() => {})
      .finally(() => setJobsLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* ── HERO ── */}
      <section className="relative overflow-hidden pt-24 pb-32 lg:pt-32 lg:pb-44"
        style={{ background: 'linear-gradient(145deg, #0d001a 0%, #360153 45%, #1a002b 100%)' }}>

        {/* Animated blobs */}
        <div className="absolute top-0 right-0 w-[700px] h-[700px] rounded-full pointer-events-none opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.6) 0%, transparent 65%)', transform: 'translate(30%, -30%)' }} />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full pointer-events-none opacity-15"
          style={{ background: 'radial-gradient(circle, rgba(234,179,8,0.4) 0%, transparent 65%)', transform: 'translate(-30%, 30%)' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full pointer-events-none opacity-10"
          style={{ background: 'radial-gradient(circle, rgba(54,1,83,0.8) 0%, transparent 60%)' }} />

        {/* Floating shapes */}
        <div className="absolute top-20 left-[10%] w-3 h-3 rounded-full bg-yellow-400/40 animate-pulse" />
        <div className="absolute top-40 right-[15%] w-2 h-2 rounded-full bg-purple-400/50 animate-pulse" style={{ animationDelay: '0.5s' }} />
        <div className="absolute bottom-32 left-[20%] w-4 h-4 rounded-full bg-white/10 animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-60 left-[40%] w-1.5 h-1.5 rounded-full bg-yellow-300/60 animate-pulse" style={{ animationDelay: '1.5s' }} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 border border-white/15"
              style={{ background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(10px)' }}>
              <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
              <span className="text-yellow-400 text-xs font-black tracking-[0.2em] uppercase">We're Hiring</span>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.05] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Build Your Career<br />
              <span className="text-transparent bg-clip-text"
                style={{ backgroundImage: 'linear-gradient(90deg, #eab308, #f59e0b, #fbbf24)' }}>
                at Fundviser Capital
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-white/60 max-w-2xl mx-auto leading-relaxed mb-10">
              Join a BSE-listed financial powerhouse where innovation meets ambition. Shape the future of India with a team that rewards ownership, celebrates growth, and invests in its people.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="#open-positions"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl font-black text-sm tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                style={{ background: 'linear-gradient(135deg, #eab308 0%, #f59e0b 100%)', color: '#1a0030', boxShadow: '0 8px 30px rgba(234,179,8,0.4)' }}>
                View Open Positions <ChevronRight className="w-4 h-4" />
              </a>
              <a href="#open-application"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl font-black text-sm tracking-wide border border-white/20 text-white transition-all duration-300 hover:bg-white/10 hover:scale-105"
                style={{ backdropFilter: 'blur(10px)' }}>
                Open Application <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>

        </div>

        {/* Wave separator */}
        <div className="absolute bottom-0 left-0 right-0" style={{ lineHeight: 0 }}>
          <svg viewBox="0 0 1440 80" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none"
            style={{ display: 'block', width: '100%', height: 80 }}>
            <path d="M0,40 C240,80 480,0 720,40 C960,80 1200,0 1440,40 L1440,80 L0,80 Z" fill="#ffffff" />
          </svg>
        </div>
      </section>

      {/* ── OPEN POSITIONS ── */}
      <section id="open-positions" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-black tracking-[0.2em] uppercase mb-6"
              style={{ background: 'rgba(54,1,83,0.08)', color: '#360153' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-[#360153] animate-pulse" />
              Open Positions
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
              Find Your <span className="text-[#360153]">Perfect Role</span>
            </h2>
            <p className="text-base text-gray-500 max-w-xl mx-auto">
              Explore our current openings and take the next step in your career journey.
            </p>
          </div>

          {jobsLoading ? (
            <div className="flex items-center justify-center gap-3 py-16 text-gray-400">
              <span className="w-5 h-5 border-2 border-gray-200 border-t-[#360153] rounded-full animate-spin" />
              Loading positions…
            </div>
          ) : null}

          <div className="space-y-5">
            {jobs.map(job => (
              <div key={job.id}
                className="group relative bg-white rounded-3xl border border-gray-100 p-6 sm:p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-[#360153]/8 hover:border-[#360153]/20 overflow-hidden">

                {/* Left accent */}
                <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-3xl bg-gradient-to-b from-[#360153] to-purple-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <span className={`px-2.5 py-1 rounded-full text-white text-xs font-bold ${job.badgeColor}`}>{job.badge}</span>
                      <span className="text-xs font-bold text-[#360153] uppercase tracking-wider">{job.department}</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#360153] transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>{job.title}</h3>
                    <div className="flex flex-wrap gap-4 text-xs text-gray-500 font-semibold">
                      <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#360153]" />{job.location}</span>
                      <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-[#360153]" />{job.type}</span>
                      <span className="flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5 text-[#360153]" />{job.experience}</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-3 leading-relaxed max-w-2xl">{job.description}</p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
                    <button
                      onClick={() => setActiveJob(job)}
                      className="px-6 py-3 rounded-xl border-2 border-[#360153]/20 text-[#360153] font-bold text-sm transition-all duration-200 hover:border-[#360153] hover:bg-[#360153]/5 hover:scale-105"
                    >
                      View Details
                    </button>
                    <button
                      onClick={() => setApplyJob(job)}
                      className="px-6 py-3 rounded-xl text-white font-bold text-sm transition-all duration-200 hover:scale-105 hover:shadow-lg"
                      style={{ background: 'linear-gradient(135deg, #360153 0%, #5a0280 100%)' }}
                    >
                      Apply Now
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CULTURE SECTION ── */}
      <section className="py-24 relative overflow-hidden"
        style={{ background: 'linear-gradient(145deg, #0d001a 0%, #360153 60%, #0d001a 100%)' }}>

        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.15) 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(234,179,8,0.4) 0%, transparent 70%)', transform: 'translate(30%, -30%)' }} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-black tracking-[0.2em] uppercase mb-6 border border-yellow-400/30"
              style={{ background: 'rgba(234,179,8,0.12)', color: '#eab308' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse" />
              Our Culture
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-5 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              Where Great Minds<br />
              <span className="text-yellow-400">Do Great Work</span>
            </h2>
            <p className="text-white/50 max-w-xl mx-auto leading-relaxed">
              Culture isn't a ping-pong table. It's how decisions are made, how people are treated, and how ambition is rewarded.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {CULTURE.map(({ icon: Icon, title, desc }) => (
              <div key={title}
                className="group p-6 rounded-2xl border border-white/10 transition-all duration-300 hover:border-yellow-400/40 hover:-translate-y-1 hover:shadow-xl cursor-default"
                style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(10px)' }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-all duration-300 group-hover:scale-110"
                  style={{ background: 'rgba(234,179,8,0.15)', border: '1px solid rgba(234,179,8,0.2)' }}>
                  <Icon className="w-6 h-6 text-yellow-400" />
                </div>
                <h3 className="text-base font-black text-white mb-2 group-hover:text-yellow-400 transition-colors">{title}</h3>
                <p className="text-sm text-white/45 leading-relaxed group-hover:text-white/65 transition-colors">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* ── TESTIMONIALS ── */}
      <section className="py-24" style={{ background: 'linear-gradient(180deg, #faf5ff 0%, #f0f4ff 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-black tracking-[0.2em] uppercase mb-6"
              style={{ background: 'rgba(54,1,83,0.08)', color: '#360153' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-[#360153] animate-pulse" />
              Hear From Our Team
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
              People Who <span className="text-[#360153]">Love It Here</span>
            </h2>
            <p className="text-base text-gray-500 max-w-xl mx-auto">Real voices from the people who choose Fundviser every day.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map(({ name, role, tenure, quote, initials, gradient }) => (
              <div key={name}
                className="group relative bg-white p-8 rounded-3xl border border-gray-100 shadow-sm transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#360153]/10 overflow-hidden">
                {/* Quote mark */}
                <div className="absolute top-5 right-6 text-7xl font-black leading-none text-gray-100 select-none group-hover:text-[#360153]/8 transition-colors">"</div>

                <p className="text-gray-600 leading-relaxed mb-6 relative z-10 text-sm">{quote}</p>

                <div className="flex items-center gap-4 relative z-10">
                  <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0 shadow-lg`}>
                    <span className="text-white font-black text-sm">{initials}</span>
                  </div>
                  <div>
                    <p className="font-black text-gray-900 text-sm">{name}</p>
                    <p className="text-xs text-[#360153] font-semibold">{role}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{tenure}</p>
                  </div>
                </div>

                {/* Star rating */}
                <div className="flex gap-1 mt-5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── OPEN APPLICATION FORM ── */}
      <section id="open-application" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">

            {/* Left info */}
            <div className="lg:sticky lg:top-28">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-black tracking-[0.2em] uppercase mb-6"
                style={{ background: 'rgba(54,1,83,0.08)', color: '#360153' }}>
                <span className="w-1.5 h-1.5 rounded-full bg-[#360153] animate-pulse" />
                Open Application
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-5 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                Don't See Your<br />
                <span className="text-[#360153]">Role Listed?</span>
              </h2>
              <p className="text-gray-500 leading-relaxed mb-8">
                We're always on the lookout for exceptional talent. Send us your profile and we'll reach out when the right opportunity arises or create one for you.
              </p>

              <div className="space-y-5">
                {[
                  { icon: Award,    title: 'We Value Talent First', desc: 'Great people get noticed regardless of open roles.' },
                  { icon: Globe,    title: 'Diverse Opportunities',  desc: 'Finance, tech, ops, legal — we hire across functions.' },
                  { icon: Building2, title: 'BSE-Listed Stability',  desc: 'Join a publicly listed, regulated, and trusted institution.' },
                ].map(({ icon: Icon, title, desc }) => (
                  <div key={title} className="flex items-start gap-4 p-5 rounded-2xl border border-gray-100 hover:border-[#360153]/20 hover:bg-[#360153]/3 transition-all duration-200 group">
                    <div className="w-10 h-10 rounded-xl bg-[#360153]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#360153] transition-colors">
                      <Icon className="w-5 h-5 text-[#360153] group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <p className="font-black text-gray-900 text-sm mb-0.5">{title}</p>
                      <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Form panel */}
            <div className="bg-white rounded-3xl shadow-2xl shadow-gray-200/80 p-8 sm:p-10" style={{ border: '1px solid #f0e8f8' }}>
              <h3 className="text-2xl font-bold text-gray-900 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>Share Your Profile</h3>
              <p className="text-sm text-gray-400 mb-8">We'll review and get back to you when the right role opens up.</p>
              <OpenApplicationForm />
            </div>
          </div>
        </div>
      </section>

      {/* ── BOTTOM CTA STRIP ── */}
      <section className="py-16 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1a002b 0%, #360153 50%, #2d0147 100%)' }}>
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.2) 1px, transparent 1px)', backgroundSize: '25px 25px' }} />
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Ready to Make Your Move?
          </h2>
          <p className="text-white/50 mb-8 max-w-xl mx-auto">
            Your next chapter starts here. Explore open roles or drop us your profile — we'd love to meet you.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="#open-positions"
              className="px-8 py-4 rounded-2xl font-black text-sm tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-2xl"
              style={{ background: 'linear-gradient(135deg, #eab308, #f59e0b)', color: '#1a0030', boxShadow: '0 8px 24px rgba(234,179,8,0.4)' }}>
              Browse Open Roles
            </a>
            <Link to="/contact"
              className="px-8 py-4 rounded-2xl font-black text-sm tracking-wide border border-white/20 text-white transition-all duration-300 hover:bg-white/10 hover:scale-105">
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      {/* Job Detail Drawer */}
      {activeJob && (
        <JobDrawer
          job={activeJob}
          onClose={() => setActiveJob(null)}
          onApply={() => { setApplyJob(activeJob); setActiveJob(null); }}
        />
      )}

      {/* Apply Modal */}
      {applyJob && (
        <ApplyModal
          job={applyJob}
          onClose={() => setApplyJob(null)}
        />
      )}

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default Careers;
